'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { PortableText } from '@portabletext/react';
import { urlFor, resolveImageSrcSet } from '@/sanity/lib/image';
import { computeTodayOpeningStatus, formatIntervals } from '@/utils/openingHours';
import { TouristAttractionJsonLd, BreadcrumbJsonLd } from '@/components/seo';
import { buildFaqJsonLd, serializeJsonLd } from '@/lib/structuredData';
import GettingThere from './GettingThere';
import { createPortableTextConfig } from '@/utils/blog/portableTextConfig';
import { replacePlaceholdersDeep } from '@/lib/content/placeholders';
import { CountryFAQ } from '@/components/country';
import GfSectionMark from '@/components/common/GfSectionMark';
import GfArticleCards from '@/components/common/GfArticleCards';
import ShareButtons from '@/components/blog/ShareButtons';
import { ICONS } from '@/components/common/gfIcons';
import GfSubnav from '@/components/common/GfSubnav';
import GfBento from '@/components/common/GfBento';
import GfCta from '@/components/common/GfCta';
import AffiliateLink from '@/components/common/AffiliateLink';
import { t } from '@/i18n/translations';
import { buildAffiliateSubId, getAffiliateMetadata } from '@/lib/affiliates/metadata';
import { wrapAffiliateLink } from '@/lib/affiliates/proxy';
import { buildProviderLink } from '@/utils/blog/providerLinkHelpers';
import InlineOfferCard from '@/components/blog/InlineOfferCard';

// ── Text processing utilities (module-level, not recreated per render) ──

const textToBlocks = (text) => {
    if (!text || typeof text !== 'string') return text;
    if (Array.isArray(text)) return text;
    return text.split(/\n/).filter(line => line.trim()).map((line, pIdx) => {
        const children = [];
        line.split(/(\*\*[^*]+\*\*)/).forEach((part, sIdx) => {
            if (!part) return;
            if (part.startsWith('**') && part.endsWith('**')) {
                children.push({ _type: 'span', _key: `s${pIdx}_${sIdx}`, text: part.slice(2, -2), marks: ['strong'] });
            } else {
                children.push({ _type: 'span', _key: `s${pIdx}_${sIdx}`, text: part, marks: [] });
            }
        });
        return { _type: 'block', _key: `b${pIdx}`, style: 'normal', children, markDefs: [] };
    });
};

const splitNewlinesInBlocks = (blocks) => {
    if (!Array.isArray(blocks)) return blocks;
    const out = []; let kc = 0;
    for (const block of blocks) {
        if (block._type !== 'block' || !Array.isArray(block.children) || !block.children.some(c => c._type === 'span' && c.text?.includes('\n'))) { out.push(block); continue; }
        let cur = [];
        for (const child of block.children) {
            if (child._type !== 'span' || !child.text?.includes('\n')) { cur.push(child); continue; }
            child.text.split('\n').forEach((line, i) => {
                if (i > 0 && cur.length > 0) { out.push({ ...block, _key: `${block._key}_n${kc++}`, children: cur }); cur = []; }
                if (line) cur.push({ ...child, _key: `${child._key || 'sp'}_n${kc++}`, text: line });
            });
        }
        if (cur.length > 0) out.push({ ...block, _key: `${block._key}_n${kc++}`, children: cur });
    }
    return out;
};

const parseBoldInBlocks = (blocks) => {
    if (!Array.isArray(blocks)) return blocks;
    return blocks.map(block => {
        if (block._type !== 'block' || !Array.isArray(block.children)) return block;
        if (!block.children.some(c => c._type === 'span' && c.text?.includes('**'))) return block;
        const nc = [];
        let bold = false;
        block.children.forEach((child, idx) => {
            if (child._type !== 'span' || !child.text?.includes('**')) {
                if (bold) nc.push({ ...child, marks: [...new Set([...(child.marks || []), 'strong'])] });
                else nc.push(child);
                return;
            }
            const parts = child.text.split('**');
            parts.forEach((part, pIdx) => {
                if (pIdx > 0) bold = !bold;
                if (!part) return;
                nc.push({
                    ...child,
                    _key: `${child._key || idx}_b${pIdx}`,
                    text: part,
                    marks: bold ? [...new Set([...(child.marks || []), 'strong'])] : (child.marks || [])
                });
            });
        });
        return { ...block, children: nc };
    });
};

const processContent = (content, gptData) => {
    if (!content) return content;
    let blocks = typeof content === 'string' ? textToBlocks(content) : content;
    blocks = splitNewlinesInBlocks(blocks);
    if (gptData) {
        blocks = replacePlaceholdersDeep(blocks, gptData);
        blocks = splitNewlinesInBlocks(blocks);
    }
    return parseBoldInBlocks(blocks);
};

const formatFullSchedule = (oh, dn) => {
    if (!oh?.seasons) return [];
    return oh.seasons.map((s) => {
        const sn = s.title || '', dr = s.from && s.to ? `${s.from.slice(5)} - ${s.to.slice(5)}` : '';
        const label = sn && dr ? `${sn} (${dr})` : sn || dr;
        const value = s.days?.map(d => `${dn[d] || d}: ${s.hours?.map(h => `${h.start}-${h.end}`).join(', ') || ''}`).join('; ') || '';
        return { label, value };
    }).filter(r => r.label || r.value);
};

export default function PoiPageContent({ poiPage, airports = [], cities = [], lang = 'ru', gptData = null, recommendedServicesPreset = null }) {
    const sp = t(lang, 'servicePage');   // общие тексты сервис-страниц
    const vp = t(lang, 'poiPageUi');      // специфика страницы достопримечательности
    const [showFullSchedule, setShowFullSchedule] = useState(false);

    const nearbyAuto = poiPage?.nearbyAuto;
    const poiCoords = poiPage?.poi?.coords;

    const sortedNearby = useMemo(() => {
        if (!Array.isArray(nearbyAuto) || !nearbyAuto.length || !poiCoords?.lat || !poiCoords?.lng) return [];
        return nearbyAuto
            .map((p) => {
                const hc = p?.coords?.lat && p?.coords?.lng;
                const km = hc ? Math.sqrt(((p.coords.lat - poiCoords.lat) ** 2) + ((p.coords.lng - poiCoords.lng) ** 2)) * 111.32 : Infinity;
                return { ...p, _km: km };
            })
            .filter(p => isFinite(p._km) && p._km > 0.05)
            .sort((a, b) => a._km - b._km)
            .slice(0, 9);
    }, [nearbyAuto, poiCoords]);

    if (!poiPage) {
        return (
            <div className="gf-root" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <h1 className="gf-h2">{vp.notFound}</h1>
            </div>
        );
    }

    const { h1, subtitle, intro, body, overview, highlights, history, visitingInfo, howToGetThere, bestTimeToVisit, faq, gallery, poi, quickSummary, sources } = poiPage;
    const openHoursText = poi?.openHours;
    const openingHours = poi?.openingHours;
    const openingCalc = openingHours ? computeTodayOpeningStatus(openingHours) : { isOpenNow: null, todayIntervals: [] };

    const citySlug = poiPage.citySlug ?? (poi?.city?.slug?.current ?? (typeof poi?.city?.slug === 'string' ? poi.city.slug : null));
    const countrySlug = poiPage.countrySlug ?? poi?.countrySlug;
    const poiSlug = poiPage?.poiSlug || poi?.slug?.current || (typeof poi?.slug === 'string' ? poi.slug : null);
    const category = String(poi?.category || '').toLowerCase();
    const isWaterPoi = /island|остров|beach|пляж|bay|залив|boat|marine|sea/.test(`${category} ${h1 || ''}`.toLowerCase());
    const ticketAffiliate = getAffiliateMetadata(poi?.ticketBookingUrl);
    const ticketHref = ticketAffiliate
        ? wrapAffiliateLink(poi.ticketBookingUrl, {
            provider: ticketAffiliate.provider,
            subId: buildAffiliateSubId({
                language: lang,
                pageSlug: `poi-${citySlug || 'unknown'}-${poiSlug || 'unknown'}`,
                position: 'poi-tickets',
            }),
        })
        : poi?.ticketBookingUrl;

    const cityName = poi?.city?.localizedTitle || poi?.city?.title || '';
    const countryName = poi?.city?.countryLocalizedName || poiPage.countryName || '';
    const breadcrumbItems = [
        { name: vp.home, url: `/${lang}` },
        ...(countrySlug && countryName ? [{ name: countryName, url: `/${lang}/${countrySlug}` }] : []),
        ...(citySlug && countrySlug && cityName ? [{ name: cityName, url: `/${lang}/${countrySlug}/${citySlug}` }] : []),
        { name: h1 || poi?.nameEn || '' }
    ].filter(item => item.name);

    // ── Processed content ──
    const proc = (content) => processContent(content, gptData);

    const introProcessed = proc(intro);
    const overviewProcessed = proc(overview) || proc(body);
    const highlightsProcessed = proc(highlights);
    const historyProcessed = proc(history);
    const howToGetThereProcessed = proc(howToGetThere);
    const bestTimeToVisitProcessed = proc(bestTimeToVisit);
    const ticketsProcessed = proc(visitingInfo?.tickets);
    const rulesProcessed = proc(visitingInfo?.rules);
    const facilitiesProcessed = proc(visitingInfo?.facilities);

    let faqItems = [];
    if (faq && Array.isArray(faq) && faq.length > 0) {
        faqItems = faq.filter(item => (item?.q || item?.question) && (item?.a || item?.answer))
            .map(item => ({ question: item.q || item.question, answer: textToBlocks(item.a || item.answer) }));
        if (gptData && faqItems.length > 0) {
            faqItems = faqItems.map(item => ({
                question: typeof item.question === 'string' ? replacePlaceholdersDeep(item.question, gptData) : item.question,
                answer: parseBoldInBlocks(replacePlaceholdersDeep(item.answer, gptData))
            }));
        }
    }

    const basePtComponents = createPortableTextConfig({
        language: lang,
        primaryCategory: null,
        tripAffiliateConfig: null,
        bookingAffiliateConfig: null,
        post: countrySlug ? { countries: [{ slug: countrySlug }] } : null
    });
    // GF-типографика поверх блогового конфига: заголовки/списки внутри prose —
    // в стиле Gallery·Field (Schibsted + тире/моно-номера), а не сериф статей.
    // Ссылки и кастомные типы (оффер-карты и т.п.) остаются из базового конфига.
    const ptComponents = {
        ...basePtComponents,
        block: {
            ...basePtComponents?.block,
            normal: ({ children }) => <p>{children}</p>,
            h2: ({ children }) => <h3 className="poi-prose-h">{children}</h3>,
            h3: ({ children }) => <h3 className="poi-prose-h">{children}</h3>,
            h4: ({ children }) => <h4 className="poi-prose-h poi-prose-h--sm">{children}</h4>,
        },
        list: {
            ...basePtComponents?.list,
            bullet: ({ children }) => <ul className="poi-prose-list">{children}</ul>,
            number: ({ children }) => <ol className="poi-prose-list poi-prose-list--num">{children}</ol>,
        },
        listItem: {
            ...basePtComponents?.listItem,
            bullet: ({ children }) => <li>{children}</li>,
            number: ({ children }) => <li>{children}</li>,
        },
    };

    const fullSchedule = formatFullSchedule(openingHours, vp.weekdays);

    const hasHours = openHoursText || openingHours?.hoursTextOverride || openingHours?.notes || openingCalc.todayIntervals?.length || openingHours;
    const hasMap = poi?.coords?.lat && poi?.coords?.lng;
    const hasCTA = poi?.ticketBookingUrl || poi?.officialSite;

    // Google-карта по place_id (точно) с фолбэком на координаты. Бесплатно:
    // встроенная карта — keyless embed (или официальный Maps Embed API, если задан ключ),
    // кнопки — deep-link'и без вызовов API.
    const gmapsEmbedKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_EMBED_KEY;
    const gPlaceId = poi?.googlePlaceId;
    const gQueryName = encodeURIComponent(h1 || poi?.nameEn || '');
    const mapEmbedSrc = !hasMap ? null : (gmapsEmbedKey
        ? `https://www.google.com/maps/embed/v1/place?key=${gmapsEmbedKey}&q=${gPlaceId ? `place_id:${gPlaceId}` : `${poi.coords.lat},${poi.coords.lng}`}&language=${lang}&zoom=15`
        : `https://maps.google.com/maps?q=${poi.coords.lat},${poi.coords.lng}&z=15&hl=${lang}&output=embed`);
    const mapOpenHref = gPlaceId
        ? `https://www.google.com/maps/search/?api=1&query=${gQueryName}&query_place_id=${gPlaceId}`
        : (hasMap ? `https://www.google.com/maps/search/?api=1&query=${poi.coords.lat},${poi.coords.lng}` : '#');
    const hasGettingTimes = hasMap;
    const directionsHref = howToGetThereProcessed ? '#getting' : (hasMap ? '#map' : '#overview');
    // Якорь #tours есть на странице города только при турах города — у Казбеги его нет, и кнопка вела в никуда
    // (обход 01.10.2026). Таблица туров страны есть всегда.
    const contextualTourHref = countrySlug ? `/${lang}/${countrySlug}/tours` : '#overview';

    const visitingCardCount = [ticketsProcessed, rulesProcessed, facilitiesProcessed].filter(Boolean).length;

    // ── Sidebar meta rows (from Sanity, not hardcoded) ──
    const metaRows = [
        typeof poi?.paidEntry === 'boolean' && {
            k: vp.entry,
            v: poi.paidEntry ? vp.entryPaid : vp.entryFree,
        },
    ].filter(Boolean);

    // ── Anchor links (only for available sections) ──
    const anchors = [
        { href: '#overview', label: vp.anchorAbout },
        hasGettingTimes && { href: '#travel', label: vp.anchorTravel },
        hasMap && { href: '#map', label: vp.anchorMap },
        highlightsProcessed && { href: '#highlights', label: vp.anchorHighlights },
        historyProcessed && { href: '#history', label: vp.anchorHistory },
        visitingCardCount > 0 && { href: '#visiting', label: vp.anchorVisiting },
        howToGetThereProcessed && { href: '#getting', label: vp.anchorGetting },
        bestTimeToVisitProcessed && { href: '#best-time', label: vp.anchorBestTime },
        faqItems.length > 0 && { href: '#faq', label: sp.anchor.faq },
        sortedNearby.length > 0 && { href: '#nearby', label: vp.anchorNearby },
    ].filter(Boolean);

    const heroImage = gallery && gallery[0]?.image ? gallery[0].image : poi?.cover?.image;
    // Был исходник без ширины — многомегабайтный LCP на телефоне (обход 30.09)
    const heroBg = heroImage ? urlFor(heroImage).width(1920).height(1080).fit('crop').url() : '/images/my-hero.jpg';
    const heroSrcSet = heroImage ? resolveImageSrcSet(heroImage, [640, 960, 1280, 1920], 16 / 9) : undefined;
    const localName = poi?.localName || null;

    // ── JSON-LD: canonical URL, absolute image, FAQPage ──
    const poiCanonical = (countrySlug && citySlug && poiSlug)
        ? `https://www.triplinkhub.com/${lang}/${countrySlug}/${citySlug}/places/${poiSlug}`
        : undefined;
    const schemaImage = heroImage ? urlFor(heroImage).width(1200).height(630).fit('crop').url() : undefined;
    const poiFaqJsonLd = faqItems.length > 0
        ? buildFaqJsonLd(faqItems, poiCanonical ? { id: `${poiCanonical}#faq` } : undefined)
        : null;

    return (
        <main className="gf-root poi-root">
            <TouristAttractionJsonLd poi={poi} poiPage={poiPage} lang={lang} url={poiCanonical} image={schemaImage} googlePlaceId={gPlaceId} />
            {breadcrumbItems.length > 1 && (
                <BreadcrumbJsonLd items={breadcrumbItems.map(item => ({ ...item, url: item.url ? `https://www.triplinkhub.com${item.url}` : undefined }))} />
            )}
            {poiFaqJsonLd && (
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(poiFaqJsonLd) }} />
            )}

            {/* ── HERO ─────────────────────────────────────────── */}
            <section className="ct-hero">
                <img src={heroBg} srcSet={heroSrcSet} sizes="100vw" fetchPriority="high" alt={h1 || poi?.nameEn || vp.attraction} />
                <div className="scrim" />
                <div className="ct-hero-in">
                    <h1>{h1 || poi?.nameEn}</h1>
                    {localName && <p className="loc">{localName}</p>}
                    {subtitle && <p className="sub">{subtitle}</p>}
                    <div className="gf-hero-cta">
                        <Link className="pri" href={directionsHref}>
                            {vp.heroDirections} <b>↓</b>
                        </Link>
                        <Link className="sec" href={contextualTourHref}>
                            {isWaterPoi ? vp.heroBoatTrip : vp.heroNearbyTour}
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── SUBNAV / BREADCRUMB ──────────────────────────── */}
            <GfSubnav lang={lang} leaf={h1 || poi?.nameEn} anchors={anchors} crumbs={[
                countrySlug && countryName && { href: `/${lang}/${countrySlug}`, label: countryName },
                citySlug && countrySlug && cityName && { href: `/${lang}/${countrySlug}/${citySlug}`, label: cityName },
            ]} />

            {/* ── 01 · OVERVIEW + SIDEBAR ──────────────────────── */}
            <section className="gf-sec" id="overview">
                <GfSectionMark eyebrow={vp.aboutEyebrow} />
                <div className={hasHours ? 'poi-2col' : 'poi-2col poi-2col--solo'}>
                    <div className="poi-main">
                        {introProcessed && (
                            <div className="poi-prose" style={{ marginTop: 0 }}>
                                <PortableText value={introProcessed} components={ptComponents} />
                            </div>
                        )}
                        {Array.isArray(quickSummary) && quickSummary.length > 0 && (
                            <aside className="article-quick-summary">
                                <p className="article-quick-summary-label">{lang === 'ru' ? 'Главное за минуту' : 'The one-minute summary'}</p>
                                <ul>
                                    {quickSummary.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                                </ul>
                            </aside>
                        )}
                        {hasCTA && (
                            <div className="poi-cta">
                                {poi?.ticketBookingUrl && (
                                    <AffiliateLink className="pri" href={ticketHref}
                                        provider={ticketAffiliate?.provider}
                                        vertical="tours" country={countrySlug} position="poi-tickets">
                                        {vp.buyTickets}
                                    </AffiliateLink>
                                )}
                                {poi?.officialSite && (
                                    <a className="sec" href={poi.officialSite} target="_blank" rel="noopener noreferrer">
                                        {vp.officialSite} ↗
                                    </a>
                                )}
                            </div>
                        )}
                    </div>

                    {hasHours && (
                        <aside className="poi-side">
                            <div className="hd">{vp.hours}</div>
                            <div className="bd">
                                {typeof openingCalc.isOpenNow === 'boolean' && (
                                    <span className="poi-status" style={openingCalc.isOpenNow ? undefined : { color: 'var(--stamp)', borderColor: 'var(--stamp)' }}>
                                        <span className="d" style={openingCalc.isOpenNow ? undefined : { background: 'var(--stamp)' }} />
                                        {openingCalc.isOpenNow ? vp.open : vp.closed}
                                    </span>
                                )}
                                {openingCalc?.todayIntervals?.length > 0 && (
                                    <p className="poi-today">{vp.today}: <b>{formatIntervals(openingCalc.todayIntervals)}</b></p>
                                )}
                                {openHoursText && <p className="poi-today" style={{ color: 'var(--muted)' }}>{openHoursText}</p>}
                                {!openHoursText && openingHours?.hoursTextOverride && (
                                    <p className="poi-today" style={{ color: 'var(--muted)' }}>{openingHours.hoursTextOverride}</p>
                                )}
                                {openingHours?.notes && (
                                    <p className="poi-today" style={{ color: 'var(--muted)', fontSize: 13 }}>{openingHours.notes}</p>
                                )}
                                {fullSchedule.length > 0 && (
                                    <>
                                        <button type="button" onClick={() => setShowFullSchedule(!showFullSchedule)}
                                            style={{ marginTop: 14, fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--pine)', background: 'none', border: 'none', borderBottom: '1px solid var(--pine)', padding: '0 0 2px', cursor: 'pointer' }}>
                                            {showFullSchedule ? vp.hide : vp.showSchedule}
                                        </button>
                                        {showFullSchedule && (
                                            <ul className="poi-sched">
                                                {fullSchedule.map((row, i) => (
                                                    <li key={i}><span className="day">{row.label}</span><span>{row.value}</span></li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                )}
                                {metaRows.length > 0 && (
                                    <ul className="poi-meta">
                                        {metaRows.map((m, i) => (
                                            <li key={i}><span className="k">{m.k}</span><span className="v">{m.v}</span></li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </aside>
                    )}
                </div>
            </section>

            {/* ── 02 · TRAVEL TIME (карточки-факты, как на страницах города) ── */}
            {hasGettingTimes && (
                <section className="gf-sec" id="travel" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.transportEyebrow} title={vp.travelTime} />
                    <GettingThere
                        poi={poi}
                        airportsIndex={airports}
                        citiesIndex={cities}
                        locale={lang}
                    />
                    {/* Без секции «Как добраться» аренда авто не показывалась вовсе — у пляжей Будвы,
                        главного города по броням Localrent (обход 01.10.2026) */}
                    {!howToGetThereProcessed && (
                        <InlineOfferCard
                            preset={recommendedServicesPreset}
                            vertical="carRental"
                            language={lang}
                            country={countrySlug}
                            postSlug={poi?.slug?.current || poi?.slug || null}
                            position="poi-travel"
                        />
                    )}
                </section>
            )}

            {/* ── MAP ──────────────────────────────────────────── */}
            {hasMap && (
                <section className="gf-sec" id="map" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.mapEyebrow} title={vp.mapTitle} />
                    <div className="gf-map gf-map--solo">
                        <div className="gf-map-frame">
                            <iframe
                                src={mapEmbedSrc}
                                title={h1 || poi?.nameEn}
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                            <a className="gf-map-btn gf-map-over" href={mapOpenHref} target="_blank" rel="noopener noreferrer">{vp.openMaps} ↗</a>
                        </div>
                    </div>
                </section>
            )}

            {/* ── 03 · DESCRIPTION ─────────────────────────────── */}
            {overviewProcessed && (
                <section className="gf-sec" id="description" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.descEyebrow} title={vp.descTitle} />
                    <div className="poi-prose">
                        <PortableText value={overviewProcessed} components={ptComponents} />
                    </div>
                    {/* Коммерческий модуль POI (аудит 2026-08-13, §8): 29% AI-сессий садятся
                        на /places без единого оффера. Туры — ставка на тренд Viator (визиты
                        ×10 за июль–август, конвертнул именно черногорский тур). Пресет уже
                        грузится в poiPage.js — новых запросов нет; карточка сама рендерит
                        null, если у страны нет активного тур-провайдера. */}
                    <InlineOfferCard
                        preset={recommendedServicesPreset}
                        vertical="tours"
                        language={lang}
                        country={countrySlug}
                        postSlug={poi?.slug?.current || poi?.slug || null}
                        position="poi-inline"
                        citySlug={citySlug}
                        cityViator={poi?.city?.viator}
                    />
                </section>
            )}

            {/* ── 04 · HIGHLIGHTS ──────────────────────────────── */}
            {highlightsProcessed && (
                <section className="gf-sec" id="highlights" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.highlightsEyebrow} title={vp.highlightsTitle} />
                    <div className="poi-prose">
                        <PortableText value={highlightsProcessed} components={ptComponents} />
                    </div>
                </section>
            )}

            {/* ── 05 · HISTORY ─────────────────────────────────── */}
            {historyProcessed && (
                <section className="gf-sec" id="history" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.historyEyebrow} title={vp.historyTitle} />
                    <div className="poi-prose">
                        <PortableText value={historyProcessed} components={ptComponents} />
                    </div>
                </section>
            )}

            {/* ── 06 · VISITOR INFO ────────────────────────────── */}
            {visitingCardCount > 0 && (
                <section className="gf-sec" id="visiting" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.visitingEyebrow} title={vp.visitingTitle} />
                    <div className="poi-info">
                        {ticketsProcessed && (
                            <article className="vi" id="visiting-tickets">
                                <div className="ic">{ICONS.creditCard}</div>
                                <h3>{vp.ticketsCard}</h3>
                                <div className="poi-prose" style={{ marginTop: 0 }}>
                                    <PortableText value={ticketsProcessed} components={ptComponents} />
                                </div>
                            </article>
                        )}
                        {rulesProcessed && (
                            <article className="vi" id="visiting-rules">
                                <div className="ic">{ICONS.shield}</div>
                                <h3>{vp.rulesCard}</h3>
                                <div className="poi-prose" style={{ marginTop: 0 }}>
                                    <PortableText value={rulesProcessed} components={ptComponents} />
                                </div>
                            </article>
                        )}
                        {facilitiesProcessed && (
                            <article className="vi" id="visiting-facilities">
                                <div className="ic">{ICONS.home}</div>
                                <h3>{vp.facilitiesCard}</h3>
                                <div className="poi-prose" style={{ marginTop: 0 }}>
                                    <PortableText value={facilitiesProcessed} components={ptComponents} />
                                </div>
                            </article>
                        )}
                    </div>
                </section>
            )}

            {/* ── 07 · HOW TO GET THERE ────────────────────────── */}
            {howToGetThereProcessed && (
                <section className="gf-sec" id="getting" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.gettingEyebrow} title={vp.gettingTitle} />
                    <div className="poi-prose">
                        <PortableText value={howToGetThereProcessed} components={ptComponents} />
                    </div>
                    {/* carRental под интентом «как добраться»: главная денежная вертикаль
                        сайта (Localrent €382 за 2026, средний чек комиссии €64). */}
                    <InlineOfferCard
                        preset={recommendedServicesPreset}
                        vertical="carRental"
                        language={lang}
                        country={countrySlug}
                        postSlug={poi?.slug?.current || poi?.slug || null}
                        position="poi-getting"
                    />
                </section>
            )}

            {/* ── 08 · BEST TIME ───────────────────────────────── */}
            {bestTimeToVisitProcessed && (
                <section className="gf-sec" id="best-time" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.bestTimeEyebrow} title={vp.bestTimeTitle} />
                    <div className="poi-prose">
                        <PortableText value={bestTimeToVisitProcessed} components={ptComponents} />
                    </div>
                </section>
            )}

            {/* ── 08b · SOURCES ────────────────────────────────── */}
            {Array.isArray(sources) && sources.length > 0 && (
                <section className="gf-sec" id="sources" style={{ paddingTop: 0 }}>
                    <div className="article-sources" style={{ marginTop: 0 }} aria-label={lang === 'ru' ? 'Источники' : 'Sources'}>
                        <h2>{lang === 'ru' ? 'Источники' : 'Sources'}</h2>
                        <ul>
                            {sources.map((src, i) => {
                                const href = src?.provider?.code
                                    ? buildProviderLink(src.provider, lang, null)
                                    : src?.url;
                                if (!href || href === '#' || !src?.label) return null;
                                return (
                                    <li key={src._key || i}>
                                        <a href={href} target="_blank" rel="noopener noreferrer nofollow">{src.label}</a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </section>
            )}

            {/* ── 09 · FAQ + SHARE ─────────────────────────────── */}
            {faqItems.length > 0 ? (
                <section className="gf-sec" id="faq" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.qa} tag={sp.tag.faq} title={sp.popularQuestions} />
                    <p className="gf-lead">{vp.faqLead}</p>
                    <div className="poi-faq-grid">
                        <CountryFAQ faqItems={faqItems} lang={lang} countryName={h1} countrySlug={countrySlug} variant="gf" />
                        <aside className="poi-faq-side">
                            <div className="poi-side2">
                                <div className="hd">{vp.share}</div>
                                <div className="bd" style={{ padding: '16px 22px 20px' }}>
                                    <ShareButtons hideLabel title={h1 || poi?.nameEn} description={subtitle || ''} categoryColor="#1E466E" language={lang} />
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>
            ) : (
                /* без FAQ — компактная полоса шеринга вместо решётки с пустой колонкой */
                <section className="gf-sec" id="share" style={{ paddingTop: 0 }}>
                    <div className="poi-share-row">
                        <span className="k">{vp.share}</span>
                        <ShareButtons hideLabel title={h1 || poi?.nameEn} description={subtitle || ''} categoryColor="#1E466E" language={lang} />
                    </div>
                </section>
            )}

            {/* ── ARTICLES (упоминания) ────────────────────────── */}
            {Array.isArray(poiPage?.mentionArticles) && poiPage.mentionArticles.length > 0 && (
                <section className="gf-sec" id="articles" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={h1 || poi?.nameEn} title={lang === 'ru' ? 'Статьи об этом месте' : 'Articles about this place'} allHref={countrySlug ? `/${lang}/blog?country=${countrySlug}` : null} allLabel={sp.allArticles} />
                    <GfArticleCards posts={poiPage.mentionArticles} lang={lang} variant="bento" bucket />
                </section>
            )}

            {/* ── CTA ──────────────────────────────────────────── */}
            {citySlug && countrySlug && cityName && (
                <GfCta
                    eyebrow={vp.ctaEyebrow(cityName)}
                    title={vp.ctaTitle}
                >
                    <Link className="gf-btn" href={`/${lang}/${countrySlug}/${citySlug}`}>{vp.ctaBtn} →</Link>
                </GfCta>
            )}

            {/* ── NEARBY ───────────────────────────────────────── */}
            {sortedNearby.length > 0 && (
                <section className="gf-sec" id="nearby">
                    <GfSectionMark eyebrow={vp.nearbyEyebrow} title={vp.nearbyTitle} allHref={citySlug && countrySlug ? `/${lang}/${countrySlug}/${citySlug}` : null} allLabel={vp.nearbyAll} />
                    <GfBento items={sortedNearby.map((p, i) => {
                        const isValid = p.page?.poiSlug && p.countrySlug && p.citySlug && p.countrySlug !== 'null' && p.citySlug !== 'null';
                        const href = isValid
                            ? `/${lang}/${p.countrySlug}/${p.citySlug}/places/${p.page.poiSlug}`
                            : (p.coords ? `https://www.google.com/maps/search/?api=1&query=${p.coords.lat},${p.coords.lng}` : '#');
                        const imgSrc = p.cover?.image ? urlFor(p.cover.image).width(760).height(480).fit('crop').url() : null;
                        const dist = (() => {
                            if (!isFinite(p._km)) return null;
                            if (p._km < 1) return vp.distMeters(Math.round(p._km * 1000));
                            return vp.distKm(Math.round(p._km));
                        })();
                        const name = p.page?.h1 || p.nameEn;
                        return { id: p._id || `near-${i}`, href, img: imgSrc, alt: name, cat: dist, title: name, excerpt: null, meta: p.localName || null };
                    })} bucket />
                </section>
            )}
        </main>
    );
}
